import React, { SyntheticEvent, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import dayjs, { Dayjs } from 'dayjs';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { ScheduleEditDialogData } from './types';
import { setScheduleTimes } from '../../../api';
import { SearchInstrumentField } from '../../../components/SearchInstrumentField';
import { ERRORS_TEXTS } from '../../../const/validation';
import { forbiddenApi } from '../../../effector/forbidden';
import { scheduleApi } from '../../../effector/schedule';
import { MarketSchedule, ScheduleNames, SecurityInfo, TradeTime } from '../../../types/marketSchedule';
import { Option } from '../../../types/option';
import { Suggestion } from '../../../types/suggestion';

type Props = {
  data: ScheduleEditDialogData;
  forbiddenStore: SecurityInfo[];
  onClose: () => void;
};

type FormFields = {
  markets: Option[];
  times: TradeTime[];
  forbidden: SecurityInfo[];
  start: string;
  end: string;
  description: string;
};

type TimeFieldProps = {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
};

const timePattern = /^(0\d|1\d|2[0-3]):[0-5]\d:[0-5]\d$/;

const validationSchema = Yup.object({
  markets: Yup.array()
    .of(
      Yup.object({
        id: Yup.string().required(),
        name: Yup.string().required(),
      }),
    )
    .min(1, 'Не выбран код торговой площадки'),
  times: Yup.array().of(
    Yup.object({
      start: Yup.string().matches(timePattern, 'Некорректный формат времени').required(ERRORS_TEXTS.required),
      end: Yup.string()
        .matches(timePattern, 'Некорректный формат времени')
        .test('is-interval', 'Конечное время интервала меньше начального', function validate(end) {
          return !end || this.parent.start < end;
        })
        .required(ERRORS_TEXTS.required),
      description: Yup.string().optional(),
    }),
  ),
  forbidden: Yup.array().of(
    Yup.object({
      key: Yup.string().required(),
      classCode: Yup.string().required(),
      name: Yup.string().required(),
    }),
  ),
  start: Yup.string()
    .transform((value) => (!value ? '' : value))
    .matches(/^$|^((0\d|1\d|2[0-3]):[0-5]\d:[0-5]\d)$/, 'Некорректный формат времени'),
  end: Yup.string()
    .transform((value) => (!value ? '' : value))
    .matches(/^$|^((0\d|1\d|2[0-3]):[0-5]\d:[0-5]\d)$/, 'Некорректный формат времени')
    .test('is-interval', 'Конечное время интервала меньше начального', function validate(end) {
      return !end || this.parent.start < end;
    })
    .when('start', {
      is: (value: string) => Boolean(value),
      then: (schema) => schema.required(ERRORS_TEXTS.required),
    }),
  description: Yup.string().optional(),
});

const dialogTitles: Record<ScheduleNames, string> = {
  [ScheduleNames.trade]: 'Расписание рабочего времени биржи',
  [ScheduleNames.signal]: 'Расписание времени подачи торговых сигналов по стратегиям',
  [ScheduleNames.rebalance]: 'Расписание времени ребалансировки клиентских портфелей',
};

const TimeTextField: React.FC<TimeFieldProps> = ({ id, name, label, value, onChange, error }) => {
  const parsedValue: Dayjs | null = value ? dayjs(`2000-01-01T${value}`) : null;

  return (
    <TimeField
      label={label}
      value={parsedValue}
      onChange={(newValue) => {
        if (!newValue || !newValue.isValid()) {
          onChange('');
          return;
        }

        onChange(newValue.format('HH:mm:ss'));
      }}
      format="HH:mm:ss"
      fullWidth
      ampm={false}
      slotProps={{
        textField: {
          id,
          name,
          error: Boolean(error),
          helperText: error,
          placeholder: '00:00:00',
        },
      }}
    />
  );
};

const intervalRowSx = {
  display: 'grid',
  gap: 2,
  alignItems: 'stretch',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'minmax(0, 1fr) auto',
  },
};

const intervalFieldsSx = {
  display: 'grid',
  gap: 1.5,
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, minmax(0, 1fr))',
  },
};

const intervalActionSx = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  alignSelf: { xs: 'flex-start', sm: 'stretch' },
  pt: { xs: 0, sm: 2 },
  minWidth: 40,
};

export const ScheduleEditDialog: React.FC<Props> = ({ data, forbiddenStore, onClose }) => {
  const [send, setSend] = useState(false);
  const [error, setError] = useState<string>();
  const addForbiddenRef = useRef<Set<string>>(new Set());
  const removeForbiddenRef = useRef<Set<string>>(new Set());

  const title = dialogTitles[data.title];
  const defaultValues = useMemo<FormFields>(
    () => ({
      markets: data.markets.map((code) => ({ id: code, name: code })),
      forbidden: [...forbiddenStore],
      times: data.times.map((time) => ({ ...time })),
      start: '',
      end: '',
      description: '',
    }),
    [data.markets, data.times, forbiddenStore],
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { isDirty },
  } = useForm<FormFields>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as never,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'times',
  });

  const markets = watch('markets');
  const forbidden = watch('forbidden');
  const draftStart = watch('start');
  const draftEnd = watch('end');
  const draftDescription = watch('description');
  const canAddDraftInterval = Boolean(draftStart && draftEnd);

  const filteredForbidden = useMemo(() => {
    const selectedCodes = new Set(markets.map((market) => market.id));
    return forbidden.filter((security) => selectedCodes.has(security.classCode));
  }, [forbidden, markets]);

  const normalizedMarkets = useMemo(() => {
    const marketMap = new Map(data.availableMarkets.map((market) => [market.id, market]));

    markets.forEach((market) => {
      if (!marketMap.has(market.id)) {
        marketMap.set(market.id, market);
      }
    });

    return Array.from(marketMap.values());
  }, [data.availableMarkets, markets]);

  const handleClose = () => {
    if (!send) {
      setError(undefined);
      addForbiddenRef.current = new Set();
      removeForbiddenRef.current = new Set();
      onClose();
    }
  };

  const onSearch = (key: string, suggestion?: Suggestion) => {
    if (getValues('forbidden').find((security) => security.key === key)) {
      return;
    }

    addForbiddenRef.current.add(key);
    removeForbiddenRef.current.delete(key);

    setValue(
      'forbidden',
      [
        ...getValues('forbidden'),
        {
          key,
          name: suggestion?.name || key,
          classCode: suggestion?.classCode ?? '',
        },
      ],
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const onRemoveForbidden = (index: number) => {
    const currentForbidden = getValues('forbidden');
    const key = currentForbidden[index]?.key;

    if (!key) {
      return;
    }

    addForbiddenRef.current.delete(key);
    removeForbiddenRef.current.add(key);
    setValue(
      'forbidden',
      currentForbidden.filter((_, currentIndex) => currentIndex !== index),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const handleAddDraftInterval = async () => {
    const isValid = await trigger(['start', 'end']);

    if (!isValid) {
      return;
    }

    append({
      start: draftStart,
      end: draftEnd,
      description: draftDescription,
    });

    setValue('start', '', { shouldDirty: true });
    setValue('end', '', { shouldDirty: true });
    setValue('description', '', { shouldDirty: true });
  };

  const onSubmit: SubmitHandler<FormFields> = async (values) => {
    setSend(true);
    setError(undefined);

    let result = [...values.times];

    if (values.start && values.end) {
      result = [
        ...result,
        {
          start: values.start,
          end: values.end,
          description: values.description || '',
        },
      ];
    }

    const scheduleData: MarketSchedule = {
      id: data.id,
      name: null,
      classCodes: values.markets.map((market) => market.id),
      addForbiddenSecurities: Array.from(addForbiddenRef.current),
      removeForbiddenSecurities: Array.from(removeForbiddenRef.current),
      tradeTimes: data.title === ScheduleNames.trade ? result : null,
      signalTimes: data.title === ScheduleNames.signal ? result : null,
      rebalanceTimes: data.title === ScheduleNames.rebalance ? result : null,
      weekends: [],
    };

    try {
      const response = await setScheduleTimes(scheduleData);

      if (response?.success) {
        scheduleApi.set(response.result);
        forbiddenApi.set([...values.forbidden]);
        addForbiddenRef.current = new Set();
        removeForbiddenRef.current = new Set();
        onClose();
        return;
      }

      setError(response?.errorMessage ?? 'Ошибка на сервере');
      setSend(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSend(false);
    }
  };

  return (
    <Dialog open onClose={send ? undefined : handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        Редактирование расписания
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={send}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Typography>{title}</Typography>

            <Controller
              name="markets"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  options={normalizedMarkets}
                  value={field.value}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_: SyntheticEvent, newValue) => {
                    field.onChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Торговые площадки"
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />

            <Stack spacing={1.5}>
              <Typography fontWeight={600}>Список запрещенных инструментов</Typography>
              <SearchInstrumentField
                classCodes={markets.map((market) => market.id)}
                showSelectedValue={false}
                disabled={false}
                onClear={() => undefined}
                onSearch={onSearch}
              />
              {filteredForbidden.length ? (
                <List sx={{ border: 1, borderColor: 'divider', borderRadius: 2, py: 0 }}>
                  {filteredForbidden.map((security) => {
                    const index = forbidden.findIndex((item) => item.key === security.key);

                    return (
                      <ListItem
                        key={security.key}
                        divider
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            color="error"
                            onClick={() => onRemoveForbidden(index)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={security.name} secondary={security.classCode} />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Alert severity="info">Для выбранных площадок запрещенные инструменты не добавлены.</Alert>
              )}
            </Stack>

            <Divider />

            <Stack spacing={1.5}>
              <Typography fontWeight={600}>Интервалы времени в формате HH:mm:ss - HH:mm:ss с описанием</Typography>

              <Stack spacing={2}>
                {fields.map((field, index) => (
                  <Box key={field.id} sx={intervalRowSx}>
                    <Stack
                      spacing={1.5}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <Box sx={intervalFieldsSx}>
                        <Controller
                          name={`times.${index}.start`}
                          control={control}
                          render={({ field: timeField, fieldState }) => (
                            <TimeTextField
                              id={`times-${index}-start`}
                              name={`times.${index}.start`}
                              label="Начало"
                              value={timeField.value}
                              onChange={timeField.onChange}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                        <Controller
                          name={`times.${index}.end`}
                          control={control}
                          render={({ field: timeField, fieldState }) => (
                            <TimeTextField
                              id={`times-${index}-end`}
                              name={`times.${index}.end`}
                              label="Завершение"
                              value={timeField.value}
                              onChange={timeField.onChange}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </Box>
                      <Controller
                        name={`times.${index}.description`}
                        control={control}
                        render={({ field: descriptionField }) => (
                          <TextField
                            label="Описание"
                            value={descriptionField.value ?? ''}
                            onChange={descriptionField.onChange}
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={3}
                          />
                        )}
                      />
                    </Stack>
                    <Box sx={intervalActionSx}>
                      <IconButton
                        type="button"
                        edge="end"
                        aria-label="delete"
                        color="error"
                        onClick={() => remove(index)}
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}

                <Box sx={intervalRowSx}>
                  <Stack
                    spacing={1.5}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'action.hover',
                    }}
                  >
                    <Box sx={intervalFieldsSx}>
                      <Controller
                        name="start"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TimeTextField
                            id="draft-start"
                            name="start"
                            label="Начало"
                            value={field.value}
                            onChange={field.onChange}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                      <Controller
                        name="end"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TimeTextField
                            id="draft-end"
                            name="end"
                            label="Завершение"
                            value={field.value}
                            onChange={field.onChange}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                    </Box>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Описание"
                          value={field.value}
                          onChange={field.onChange}
                          fullWidth
                          multiline
                          minRows={3}
                          maxRows={3}
                        />
                      )}
                    />
                  </Stack>
                  <Box sx={intervalActionSx}>
                    <IconButton
                      type="button"
                      aria-label="add"
                      color="primary"
                      disabled={!canAddDraftInterval}
                      onClick={handleAddDraftInterval}
                      sx={{ mt: 0.5 }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Stack>

              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={send}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={send || (!isDirty && !canAddDraftInterval)}
            startIcon={send ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
